/**
 * Selector for the element to be displayed in fullscreen mode
 */
const fullscreenContainerSelector = `.fullscreen-container`;

/**
 * Returns the fullscreen container element if there is any in the current document,
 * or the document body otherwise
 */
function getFullscreenContainer(element?: string) {
    return (
        document.querySelector(element || fullscreenContainerSelector) ||
        document.body
    );
}

/**
 * Returns the root element on the screen:
 * the fullscreen element if in fullscreen mode, or the document body otherwise
 */
const getRootElement = () =>
    isFullscreen() ? getFullscreenElement() : document.body;

/**
 * Wrapper around document.fullscreenEnabled for cross-browser usage
 */
function isFullscreenAvailable() {
    return (
        document.fullscreenEnabled ||
        //@ts-ignore: feature detection
        document.webkitFullscreenEnabled ||
        //@ts-ignore: feature detection
        document.mozFullScreenEnabled ||
        //@ts-ignore: feature detection
        document.msFullscreenEnabled
    );
}

/**
 * Returns true if there is an element in full screen mode
 */
function isFullscreen() {
    return getFullscreenElement() ? true : false;
}

/**
 * Wrapper around document.fullscreenElement for cross-browser usage
 */
function getFullscreenElement() {
    return (
        document.fullscreenElement ||
        //@ts-ignore: feature detection
        document.webkitFullscreenElement ||
        //@ts-ignore: feature detection
        document.mozFullScreenElement ||
        //@ts-ignore: feature detection
        document.msFullscreenElement
    );
}

/**
 * Wrapper around document.requestFullscreen for cross-browser usage
 * @param {*} element The element to be displayed in full screen
 */
async function requestFullscreen(element: Element | HTMLElement) {
    if (element.requestFullscreen) {
        return element.requestFullscreen();
        //@ts-ignore: feature detection
    } else if (element.webkitRequestFullscreen) {
        //@ts-ignore: feature detection
        return element.webkitRequestFullscreen();
        //@ts-ignore: feature detection
    } else if (element.mozRequestFullScreen) {
        //@ts-ignore: feature detection
        return element.mozRequestFullScreen();
        //@ts-ignore: feature detection
    } else if (element.msRequestFullscreen) {
        //@ts-ignore: feature detection
        return element.msRequestFullscreen();
    }
}

/**
 * Wrapper around document.exitFullscreen for cross-browser usage
 */
async function exitFullscreen() {
    if (document.exitFullscreen) {
        return document.exitFullscreen();
        //@ts-ignore: feature detection
    } else if (document.webkitExitFullscreen) {
        //@ts-ignore: feature detection
        return document.webkitExitFullscreen();
        //@ts-ignore: feature detection
    } else if (document.mozCancelFullScreen) {
        //@ts-ignore: feature detection
        return document.mozCancelFullScreen();
        //@ts-ignore: feature detection
    } else if (document.msExitFullscreen) {
        //@ts-ignore: feature detection
        return document.msExitFullscreen();
    }
}

function onFullscreenChange(handler: (isFullScreen: boolean) => void) {
    const listener = () => handler(isFullscreen());
    addChangeListener(listener);
    return () => removeChangeListener(listener);
}

/**
 * Adds a listener to 'fullscreenchange' events with cross-browser support
 * @param {*} handler Event handler function to add
 */
function addChangeListener(handler: EventListener) {
    toggleChangeListener(withFunction(document.addEventListener), handler);
}

/**
 * Removes a listener subscribed to 'fullscreenchange' events with cross-browser support
 * @param {*} handler Event handler function to remove
 */
function removeChangeListener(handler: EventListener) {
    toggleChangeListener(withFunction(document.removeEventListener), handler);
}

function toggleChangeListener(
    toggle: ReturnType<typeof withFunction>,
    handler: EventListener
) {
    toggle(`fullscreenchange`, handler) ||
        toggle(`webkitfullscreenchange`, handler) ||
        toggle(`mozfullscreenchange`, handler) ||
        toggle(`MSFullscreenChange`, handler);
}

const withFunction =
    (
        toggleFn:
            | typeof document.addEventListener
            | typeof document.removeEventListener
    ) =>
    (eventName: string, handler: EventListener) => {
        if (`on${eventName}` in document) {
            toggleFn(eventName, handler);
            return true;
        }
        return false;
    };

export {
    fullscreenContainerSelector,
    isFullscreenAvailable,
    isFullscreen,
    getFullscreenContainer,
    getFullscreenElement,
    requestFullscreen,
    exitFullscreen,
    addChangeListener,
    removeChangeListener,
    onFullscreenChange,
    getRootElement
};
